import { ROUTES } from "@/shared/config/routes";
import { supportedLocales } from "@/shared/lib/i18n/config";
import { detectLocaleByNavigator } from "@/shared/lib/i18n/countryLocaleMap";
import { localeStorage } from "@/shared/lib/locale-storage";
import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AuthLayout } from "@/widgets/layout/AuthLayout";
import { BaseLayout } from "@/widgets/layout/BaseLayout";

// Trading — main page, keep static for instant load
import { Trading } from "@/features/trading/Trading";

// Lazy-loaded pages
const AuthPage = lazy(() => import("@/features/auth/AuthPage").then((m) => ({ default: m.AuthPage })));
const ForgotPassword = lazy(() =>
  import("@/features/auth/ForgotPassword").then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() => import("@/features/auth/ResetPassword").then((m) => ({ default: m.ResetPassword })));
const Wallet = lazy(() => import("@/features/wallet/Wallet").then((m) => ({ default: m.Wallet })));
const Settings = lazy(() => import("@/features/settings/Settings").then((m) => ({ default: m.Settings })));
const History = lazy(() => import("@/features/history/History").then((m) => ({ default: m.History })));
const ConfirmEmailPage = lazy(() => import("@/pages/ConfirmEmailPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));
const InstallAppPage = lazy(() => import("@/pages/install-app/InstallAppPage"));
const AccessDeniedPage = lazy(() => import("@/pages/AccessDeniedPage").then((m) => ({ default: m.AccessDeniedPage })));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage").then((m) => ({ default: m.MaintenancePage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
const EmailUnsubscribePage = lazy(() => import("@/pages/EmailUnsubscribePage"));
const CopyTrading = lazy(() => import("@/features/copy-trading/CopyTrading").then((m) => ({ default: m.CopyTrading })));
const HelpCenter = lazy(() => import("@/features/help-center/HelpCenter").then((m) => ({ default: m.HelpCenter })));
const RestoreAccountPage = lazy(() => import("@/pages/RestoreAccountPage"));
const SessionCounterWidgetPage = lazy(() =>
  import("@/pages/SessionCounterWidgetPage").then((m) => ({ default: m.SessionCounterWidgetPage })),
);
const SupportService = lazy(() =>
  import("@/features/help-center/pages/SupportService").then((m) => ({ default: m.SupportService })),
);
const GuidesAndTutorials = lazy(() =>
  import("@/features/help-center/pages/GuidesAndTutorials").then((m) => ({ default: m.GuidesAndTutorials })),
);
const Applications = lazy(() =>
  import("@/features/help-center/pages/Applications").then((m) => ({ default: m.Applications })),
);

type AppLocale = (typeof supportedLocales)[number];

const isSupportedLocale = (value: string): value is AppLocale => {
  return (supportedLocales as readonly string[]).includes(value);
};

function getLocale(): string {
  const saved = localeStorage.get();
  console.log("[getLocale] localStorage locale:", saved);
  console.log("[getLocale] navigator.language:", navigator.language);
  console.log("[getLocale] navigator.languages:", Array.from(navigator.languages ?? []));
  if (saved && isSupportedLocale(saved)) {
    console.log("[getLocale] → using saved:", saved);
    return saved;
  }
  const detected = detectLocaleByNavigator(supportedLocales);
  console.log("[getLocale] → detected from navigator:", detected);
  return detected;
}

function LocaleRedirect({ to }: { to: string }) {
  return <Navigate to={`/${getLocale()}${to}`} replace />;
}

function LocaleRedirectWithSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`/${getLocale()}${to}${location.search}`} replace />;
}

export function AppRoutes() {
  return (
    <Suspense>
      <Routes>
        {supportedLocales.map((locale) => (
          <React.Fragment key={locale}>
            <Route path={`/${locale}`} element={<Navigate to={`/${locale}${ROUTES.HOME}`} replace />} />

            {/* Auth layout routes */}
            <Route element={<AuthLayout />}>
              <Route path={`/${locale}${ROUTES.SIGN_IN}`} element={<AuthPage />} />
              <Route path={`/${locale}${ROUTES.SIGN_UP}`} element={<AuthPage />} />
              <Route path={`/${locale}${ROUTES.FORGOT_PASSWORD}`} element={<ForgotPassword />} />
              <Route path={`/${locale}${ROUTES.RESET_PASSWORD}`} element={<ResetPassword />} />
            </Route>

            {/* Base layout routes */}
            <Route element={<BaseLayout />}>
              <Route path={`/${locale}${ROUTES.HOME}`} element={<Trading />} />
              <Route path={`/${locale}${ROUTES.WALLET}`} element={<Wallet />} />
              <Route path={`/${locale}${ROUTES.SETTINGS}`} element={<Settings />} />
              <Route path={`/${locale}${ROUTES.HISTORY}`} element={<History />} />
              <Route path={`/${locale}${ROUTES.COPY_TRADING}`} element={<CopyTrading />} />
              <Route path={`/${locale}${ROUTES.HELP_CENTER}`} element={<HelpCenter />} />
              <Route path={`/${locale}${ROUTES.HELP_CENTER}/support`} element={<SupportService />} />
              <Route path={`/${locale}${ROUTES.HELP_CENTER}/guides`} element={<GuidesAndTutorials />} />
              <Route path={`/${locale}${ROUTES.HELP_CENTER}/applications`} element={<Applications />} />
              <Route path={`/${locale}/confirm-email`} element={<ConfirmEmailPage />} />
              <Route path={`/${locale}${ROUTES.PAYMENT_SUCCESS}`} element={<PaymentSuccessPage />} />
              <Route path={`/${locale}${ROUTES.PAYMENT_CANCEL}`} element={<PaymentCancelPage />} />
            </Route>

            {/* Standalone */}
            <Route path={`/${locale}${ROUTES.RESTORE_ACCOUNT}`} element={<RestoreAccountPage />} />
            <Route path={`/${locale}${ROUTES.SESSION_COUNTER_WIDGET}`} element={<SessionCounterWidgetPage />} />
            <Route path={`/${locale}${ROUTES.INSTALL_APP}`} element={<InstallAppPage />} />
            <Route path={`/${locale}${ROUTES.ACCESS_DENIED}`} element={<AccessDeniedPage />} />
            <Route path={`/${locale}${ROUTES.MAINTENANCE}`} element={<MaintenancePage />} />
            <Route path={`/${locale}${ROUTES.NOT_FOUND}`} element={<NotFoundPage />} />
            <Route
              path={`/${locale}${ROUTES.STATS_UNSUBSCRIBE}`}
              element={<EmailUnsubscribePage namespace="stats-unsubscribe" resubscribeCategory="stats" />}
            />
            <Route
              path={`/${locale}${ROUTES.NEWS_UNSUBSCRIBE}`}
              element={<EmailUnsubscribePage namespace="news-unsubscribe" resubscribeCategory="news" />}
            />
          </React.Fragment>
        ))}

        <Route path={ROUTES.HOME} element={<LocaleRedirect to={ROUTES.HOME} />} />
        <Route path={ROUTES.SIGN_IN} element={<LocaleRedirect to={ROUTES.SIGN_IN} />} />
        <Route path={ROUTES.SIGN_UP} element={<LocaleRedirectWithSearch to={ROUTES.SIGN_UP} />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<LocaleRedirect to={ROUTES.FORGOT_PASSWORD} />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<LocaleRedirectWithSearch to={ROUTES.RESET_PASSWORD} />} />
        <Route path="/confirm-email" element={<LocaleRedirectWithSearch to="/confirm-email" />} />
        <Route path={ROUTES.WALLET} element={<LocaleRedirect to={ROUTES.WALLET} />} />
        <Route path={ROUTES.SETTINGS} element={<LocaleRedirect to={ROUTES.SETTINGS} />} />
        <Route path={ROUTES.HISTORY} element={<LocaleRedirect to={ROUTES.HISTORY} />} />
        <Route path={ROUTES.COPY_TRADING} element={<LocaleRedirect to={ROUTES.COPY_TRADING} />} />
        <Route path={ROUTES.HELP_CENTER} element={<LocaleRedirect to={ROUTES.HELP_CENTER} />} />
        <Route path={ROUTES.PAYMENT_SUCCESS} element={<LocaleRedirect to={ROUTES.PAYMENT_SUCCESS} />} />
        <Route path={ROUTES.PAYMENT_CANCEL} element={<LocaleRedirect to={ROUTES.PAYMENT_CANCEL} />} />
        <Route path={ROUTES.ACCESS_DENIED} element={<LocaleRedirect to={ROUTES.ACCESS_DENIED} />} />
        <Route path={ROUTES.MAINTENANCE} element={<LocaleRedirect to={ROUTES.MAINTENANCE} />} />
        <Route path={ROUTES.STATS_UNSUBSCRIBE} element={<LocaleRedirectWithSearch to={ROUTES.STATS_UNSUBSCRIBE} />} />
        <Route path={ROUTES.NEWS_UNSUBSCRIBE} element={<LocaleRedirectWithSearch to={ROUTES.NEWS_UNSUBSCRIBE} />} />
        <Route path={ROUTES.RESTORE_ACCOUNT} element={<LocaleRedirectWithSearch to={ROUTES.RESTORE_ACCOUNT} />} />
        <Route
          path={ROUTES.SESSION_COUNTER_WIDGET}
          element={<LocaleRedirectWithSearch to={ROUTES.SESSION_COUNTER_WIDGET} />}
        />

        <Route path="*" element={<LocaleRedirect to={ROUTES.NOT_FOUND} />} />
      </Routes>
    </Suspense>
  );
}
